import { type FC, useEffect, useRef, useState } from "react";
import { Feature, RecordParticipationTypes, RemoveFeature, useFeatureParticipationRecorder } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { cookieStore } from "src/shared/stores";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { DAY_MS } from "src/website-apps/shared/constants/time";
import { type AnimationState, bannerStyles } from "./styles";

interface BannerProps {
  animationState: AnimationState;
}

const Banner: FC<BannerProps> = ({ animationState }) => {
  const storePageEvent = useStorePageEvent();
  const hasTrackedImpressionRef = useRef(false);

  useEffect(() => {
    if (animationState !== "entering" || hasTrackedImpressionRef.current) {
      return;
    }

    hasTrackedImpressionRef.current = true;
    storePageEvent(PE_TYPE.OPEN, PE_KEYS.WELCOME_BACK_BANNER_IMPRESSION);
  }, [animationState, storePageEvent]);

  return (
    <div className={bannerStyles.container} data-state={animationState}>
      <div className={bannerStyles.content} data-state={animationState}>
        {/* "Welcome back!" */}
        <Content id="cid$#3203482#" />
      </div>
    </div>
  );
};

const VISIBILITY_DELAY_MS = 3000;
const UNMOUNT_DELAY_MS = 4000;

export const WelcomeBackBanner: FC = () => {
  const appContext = useAppContext();
  const { env } = appContext;
  const isStubhub = useIsStubhub();
  const [animationState, setAnimationState] = useState<AnimationState>("hidden");
  const [isRendered, setIsRendered] = useState(true);
  const [recentlySeen, setRecentlySeen] = useState(false);
  const recordWelcomeBackToast = useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalWelcomeSnackbar);
  const mountedRef = useRef(false);

  useEffect(() => {
    try {
      if (cookieStore.get("swb", env)) {
        setRecentlySeen(true);
      } else {
        cookieStore.add("swb", Date.now().toString(), env, false, new Date(Date.now() + DAY_MS));
        recordWelcomeBackToast();
      }
    } catch (error) {
      console.error("Error getting cookie for welcome back banner", error);
      setRecentlySeen(false);
    }
  }, [env, recordWelcomeBackToast]);

  // Entrance: double rAF to ensure initial hidden state is painted before transition
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimationState("entering");
      });
    });
  }, []);

  // Exit: after visibility delay, trigger exit animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationState("exiting");
    }, VISIBILITY_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Unmount: after unmount delay, remove from DOM
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsRendered(false);
    }, UNMOUNT_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!isRendered) return null;

  return (
    <Feature
      id={FeatureSetEnum.TMGlobalWelcomeSnackbar}
      recordType={RecordParticipationTypes.MANUAL}
      control={() => (
        <RemoveFeature
          id={FeatureSetEnum.EventDetailSHWelcomeBackOnceADay.concat(FeatureSetEnum.EventDetailVGGWelcomeBackOnceADay)}
          canParticipate={() => recentlySeen}
          feature={() =>
            isStubhub ? (
              <Banner animationState={animationState} />
            ) : (
              <div className={bannerStyles.wrapper}>
                <Banner animationState={animationState} />
              </div>
            )
          }
        />
      )}
      variant={() => null}
    />
  );
};
